import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useCart } from '../context/CartContext';
import { Search, Plus, Check, X, ChevronLeft, ChevronRight, Utensils, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';

const Menu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedSettings, setSelectedSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(false);
  const [viewingImages, setViewingImages] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { addToCart } = useCart();

  const categories = ['all', 'desserts', 'beverages', 'baked items', 'main course'];

  const [availabilityType, setAvailabilityType] = useState('ready-made');

  useEffect(() => {
    fetchMenuItems();
  }, []);

  useEffect(() => {
    let items = menuItems;

    // Filter by Availability (Ready vs Pre-order)
    items = items.filter(item => item.availability === availabilityType);

    if (selectedCategory !== 'all') {
      items = items.filter(item => item.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
      );
    }

    setFilteredItems(items);
  }, [selectedCategory, searchQuery, menuItems, availabilityType]);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get('/menu');
      const data = response.data;
      const items = Array.isArray(data) ? data : (data.menuItems || []);
      const availableItems = items.filter(item => item.is_available === true || item.is_available === 1);
      setMenuItems(availableItems);
      setFilteredItems(availableItems.filter(item => item.availability === 'ready-made')); // Default init filter
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching menu:', error);
      setLoading(false);
    }
  };

  const handleAddToCart = (item) => {
    const itemWithId = { ...item, _id: item.id || item._id };
    setSelectedItem(itemWithId);
    setQuantity(1);
    setSelectedOptions([]);
    setSelectedSettings({});
    setAddedToCart(false);
  };

  const confirmAddToCart = () => {
    if (selectedItem) {
      addToCart(selectedItem, quantity, selectedOptions, selectedSettings);
      setAddedToCart(true);
      setTimeout(() => {
        setSelectedItem(null);
        setAddedToCart(false);
      }, 1500);
    }
  };

  const handleSettingChange = (settingName, option) => {
    setSelectedSettings({
      ...selectedSettings,
      [settingName]: option
    });
  };

  const toggleOption = (option) => {
    setSelectedOptions(prev => {
      const exists = prev.find(opt => opt.name === option.name);
      if (exists) {
        return prev.filter(opt => opt.name !== option.name);
      }
      return [...prev, option];
    });
  };

  const getUnitPrice = () => {
    if (!selectedItem) return 0;
    const base = Number(selectedItem.price) || 0;
    const opts = selectedOptions.reduce((sum, opt) => sum + (Number(opt.additionalPrice) || 0), 0);
    return base + opts;
  };

  const calculateTotal = () => {
    return getUnitPrice() * quantity;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gold-400 text-xl font-medium">Preparing Menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-900/10 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gold-500 mb-4 font-serif relative inline-block">
            Our Menu
            <span className="absolute -bottom-2 left-1/2 w-1/2 h-1 bg-gold-500 -translate-x-1/2 rounded-full opacity-70"></span>
          </h1>
          <p className="text-gray-400 mt-4 text-lg">Discover our curated selection of fine dishes</p>
        </motion.div>

        {/* Search & Filter */}
        <div className="max-w-4xl mx-auto mb-12 space-y-8">
          {/* Availability Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-900/50 p-1 rounded-full border border-gold-500/30 flex relative">
              <motion.div
                layout
                className="absolute top-1 bottom-1 bg-gold-500 rounded-full shadow-lg"
                initial={false}
                animate={{
                  left: availabilityType === 'ready-made' ? '4px' : '50%',
                  width: 'calc(50% - 4px)'
                }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
              <button
                onClick={() => setAvailabilityType('ready-made')}
                className={cn(
                  "relative z-10 px-8 py-2 rounded-full font-bold text-sm md:text-base w-40 transition-colors",
                  availabilityType === 'ready-made' ? "text-black" : "text-gray-400 hover:text-white"
                )}
              >
                Ready to Eat
              </button>
              <button
                onClick={() => setAvailabilityType('pre-order')}
                className={cn(
                  "relative z-10 px-8 py-2 rounded-full font-bold text-sm md:text-base w-40 transition-colors",
                  availabilityType === 'pre-order' ? "text-black" : "text-gray-400 hover:text-white"
                )}
              >
                Pre-Order
              </button>
            </div>
          </div>

          <div className="relative group">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-gold-500 transition-colors w-5 h-5" />
            <Input
              type="text"
              placeholder="Search for delicious dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-gray-900/50 border-gold-500/30 focus:border-gold-500 text-lg rounded-full shadow-lg"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category, index) => (
              <motion.button
                key={category}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  "px-8 py-3 rounded-full font-medium capitalize transition-all duration-300 transform hover:-translate-y-1 text-sm md:text-base border backdrop-blur-md relative overflow-hidden group",
                  selectedCategory === category
                    ? "bg-gold-500 text-black border-gold-500 shadow-neon-gold font-bold"
                    : "bg-white/5 text-gray-300 border-white/10 hover:border-gold-500/50 hover:text-white"
                )}
              >
                <span className="relative z-10">{category}</span>
                {selectedCategory !== category && (
                  <div className="absolute inset-0 bg-gold-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Menu Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode='popLayout'>
            {filteredItems.map((item) => (
              <motion.div
                layout
                key={item.id || item._id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="group"
              >
                <Card className="glass-card overflow-hidden group h-full flex flex-col relative border-white/5 bg-black/40 hover:bg-black/60 transition-all duration-500 hover:shadow-[0_0_30px_rgba(234,179,8,0.15)] hover:border-gold-500/30">
                  <div
                    className="relative h-64 overflow-hidden cursor-pointer"
                    onClick={() => {
                      const allImages = [item.image, ...(item.additional_images || [])].filter(Boolean);
                      if (allImages.length > 0) {
                        setViewingImages(allImages);
                        setCurrentImageIndex(0);
                      }
                    }}
                  >
                    {item.image ? (
                      <>
                        <div className="absolute inset-0 bg-gold-500/10 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none mix-blend-overlay" />
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover transform group-hover:scale-110 group-hover:rotate-1 transition-transform duration-700 ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />

                        <div className="absolute top-3 right-3 z-20">
                          <span className={cn(
                            "text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-xl backdrop-blur-md border border-white/10 flex items-center gap-1.5",
                            item.availability === 'pre-order'
                              ? "bg-purple-500/90 text-white shadow-purple-500/20"
                              : "bg-emerald-500/90 text-white shadow-emerald-500/20"
                          )}>
                            <span className={cn("w-1.5 h-1.5 rounded-full animate-pulse", item.availability === 'pre-order' ? "bg-white" : "bg-white")} />
                            {item.availability === 'pre-order' ? 'Pre-Order' : 'Ready'}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gray-900 flex flex-col items-center justify-center text-gray-700 gap-2 group-hover:bg-gray-800 transition-colors">
                        <Utensils className="w-12 h-12 opacity-20" />
                        <span className="text-xs uppercase tracking-widest opacity-40">No Image</span>
                      </div>
                    )}
                  </div>

                  <CardContent className="p-6 flex-1 flex flex-col relative z-20">
                    <div className="mb-4">
                      <div className="flex justify-between items-start gap-4 mb-2">
                        <h3 className="text-xl font-bold text-white group-hover:text-gold-400 transition-colors font-serif tracking-wide leading-tight">
                          {item.name}
                        </h3>
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-2 leading-relaxed font-light group-hover:text-gray-300 transition-colors">
                        {item.description}
                      </p>
                    </div>

                    <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Price</span>
                        <span className="text-2xl font-bold text-gold-500 font-serif">
                          <span className="text-lg align-top mr-0.5">Rs.</span>
                          {Math.round(item.price).toLocaleString()}
                        </span>
                      </div>

                      <Button
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}
                        className="h-10 px-6 rounded-full bg-white/5 hover:bg-gold-500 text-gold-400 hover:text-black border border-gold-500/30 hover:border-gold-500 transition-all duration-300 font-bold tracking-wide group/btn"
                      >
                        <span className="mr-2 group-hover/btn:mr-0 transition-all duration-300">Add</span>
                        <Plus className="w-4 h-4 ml-1 group-hover/btn:scale-0 group-hover/btn:w-0 transition-all duration-300" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredItems.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-24 bg-gray-900/30 rounded-3xl border border-dashed border-gray-700 mt-8"
          >
            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-600">
              <Filter className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-gray-300 mb-2">No items found</h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              We couldn't find any items matching your search. Try adjusting your filters.
            </p>
            <Button
              variant="link"
              className="text-gold-500 mt-4"
              onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
            >
              Clear all filters
            </Button>
          </motion.div>
        )}
      </div>

      {/* Add Item Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-gray-900 border border-gold-500/30 rounded-2xl w-full max-w-lg shadow-2xl relative overflow-hidden"
            >
              {/* Modal Header Image */}
              <div className="h-32 bg-gray-800 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900" />
                {selectedItem.image && (
                  <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover opacity-50" />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 text-white hover:bg-white/10 rounded-full"
                  onClick={() => setSelectedItem(null)}
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>

              <div className="p-6 md:p-8 -mt-12 relative z-10">
                {addedToCart ? (
                  <div className="py-12 text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30"
                    >
                      <Check className="w-10 h-10 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-white mb-2">Added to Cart!</h3>
                    <p className="text-gray-400">Delicious choice.</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-6">
                      <div className="flex justify-between items-start mb-2">
                        <h2 className="text-2xl font-bold text-white">{selectedItem.name}</h2>
                        <span className="text-xl font-bold text-gold-500">
                          Rs. {Math.round(getUnitPrice()).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed">{selectedItem.description}</p>
                    </div>

                    {/* Options */}
                    <div className="space-y-6 mb-8 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
                      {selectedItem.options?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-bold text-gold-400 uppercase tracking-wider mb-3">Add Ons</h3>
                          <div className="space-y-2">
                            {selectedItem.options.map((option, idx) => (
                              <label
                                key={idx}
                                className={cn(
                                  "flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all",
                                  selectedOptions.some(opt => opt.name === option.name)
                                    ? "bg-gold-500/10 border-gold-500/50"
                                    : "bg-gray-800 border-gray-700 hover:border-gray-600"
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={cn(
                                    "w-5 h-5 rounded border flex items-center justify-center transition-colors",
                                    selectedOptions.some(opt => opt.name === option.name)
                                      ? "bg-gold-500 border-gold-500"
                                      : "border-gray-500"
                                  )}>
                                    {selectedOptions.some(opt => opt.name === option.name) && <Check className="w-3 h-3 text-black" />}
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={selectedOptions.some(opt => opt.name === option.name)}
                                    onChange={() => toggleOption(option)}
                                    className="hidden"
                                  />
                                  <span className="text-gray-200 text-sm font-medium">{option.name}</span>
                                </div>
                                <span className="text-gold-400 text-sm font-semibold">+Rs. {Math.round(option.additionalPrice)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Settings */}
                      {selectedItem.settings?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-3">Customize</h3>
                          <div className="space-y-4">
                            {selectedItem.settings.map((setting, idx) => (
                              <div key={idx} className="bg-purple-900/10 border border-purple-500/20 rounded-xl p-4">
                                <div className="text-purple-300 font-medium mb-3 text-sm">{setting.name}</div>
                                <div className="flex flex-wrap gap-2">
                                  {setting.options?.map((option, optIdx) => (
                                    <label
                                      key={optIdx}
                                      className={cn(
                                        "px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-all border",
                                        selectedSettings[setting.name] === option
                                          ? "bg-purple-600 text-white border-purple-500 shadow-lg shadow-purple-500/20"
                                          : "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
                                      )}
                                    >
                                      <input
                                        type="radio"
                                        name={`setting-${idx}`}
                                        value={option}
                                        checked={selectedSettings[setting.name] === option}
                                        onChange={() => handleSettingChange(setting.name, option)}
                                        className="hidden"
                                      />
                                      {option}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-6 border-t border-white/10 mt-auto">
                      <div className="flex items-center gap-4 bg-gray-800 rounded-full p-1">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-700 text-white transition-colors"
                        >
                          <span className="text-xl font-bold mb-1">-</span>
                        </button>
                        <span className="text-white font-bold w-4 text-center">{quantity}</span>
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-700 text-white transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <Button
                        onClick={confirmAddToCart}
                        size="lg"
                        className="bg-gold-500 text-black hover:bg-gold-400 hover:scale-105 rounded-full px-8 font-bold shadow-lg shadow-gold-500/20"
                      >
                        Add • Rs. {Math.round(calculateTotal()).toLocaleString()}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Gallery */}
      <AnimatePresence>
        {viewingImages && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-xl"
            onClick={() => setViewingImages(null)}
          >
            <div className="relative max-w-5xl w-full h-[80vh] flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0 right-0 text-white hover:bg-white/10 rounded-full z-50"
                onClick={() => setViewingImages(null)}
              >
                <X className="w-8 h-8" />
              </Button>

              <div className="relative w-full h-full flex items-center justify-center">
                <AnimatePresence mode='wait'>
                  <motion.img
                    key={currentImageIndex}
                    src={viewingImages[currentImageIndex]}
                    alt={`View ${currentImageIndex + 1}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
                  />
                </AnimatePresence>

                {viewingImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + viewingImages.length) % viewingImages.length); }}
                      className="absolute left-4 bg-black/50 hover:bg-gold-500 hover:text-black text-white p-3 rounded-full transition-all backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % viewingImages.length); }}
                      className="absolute right-4 bg-black/50 hover:bg-gold-500 hover:text-black text-white p-3 rounded-full transition-all backdrop-blur-sm"
                    >
                      <ChevronRight className="w-8 h-8" />
                    </button>
                  </>
                )}
              </div>

              {viewingImages.length > 1 && (
                <div className="absolute -bottom-20 flex gap-2 overflow-x-auto max-w-full py-2 px-4 scrollbar-hide">
                  {viewingImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                      className={cn(
                        "relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0",
                        index === currentImageIndex ? "border-gold-500 scale-110" : "border-transparent opacity-50 hover:opacity-100"
                      )}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Menu;



